import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, CheckCircle2, FileText, History, Plus, Save, Sparkles, Trash2, Wand2 } from "lucide-react";
import { apiRequest } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const TEMPLATE_TYPES = [
  { value: "nomina_rspp", label: "Nomina RSPP" },
  { value: "nomina_aspp", label: "Nomina ASPP" },
  { value: "nomina_addetto_antincendio", label: "Nomina addetto antincendio" },
  { value: "nomina_addetto_primo_soccorso", label: "Nomina addetto primo soccorso" },
  { value: "verbale_consultazione_rls", label: "Verbale consultazione RLS" },
  { value: "verbale_riunione_periodica", label: "Verbale riunione periodica" },
  { value: "lettera_incarico_medico_competente", label: "Lettera incarico medico competente" },
  { value: "comunicazione_rischi_residui", label: "Comunicazione rischi residui" },
  { value: "comunicazione_dpi", label: "Comunicazione DPI" },
  { value: "altro", label: "Altro" },
];

const emptyForm = { name: "", template_type: "nomina_rspp", description: "", body: "", is_active: true };

export default function DocumentTemplates() {
  const { user } = useAuth();
  const { t: tr } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [renderState, setRenderState] = useState({ templateId: "", companyId: "", variables: {}, save: false, document_name: "", result: null });
  const [history, setHistory] = useState({ templateId: "", versions: [], renderLogs: [] });
  const canManage = ["SUPER_ADMIN", "CONSULENTE"].includes(user.role);
  const canAudit = ["SUPER_ADMIN", "CONSULENTE"].includes(user.role);

  const load = async () => {
    try {
      const [t, c] = await Promise.all([apiRequest("/document-templates"), apiRequest("/companies")]);
      setTemplates(t);
      setCompanies(c);
    } catch (err) { setError(err.message); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setInfo("");
    try {
      if (editingId) {
        await apiRequest(`/document-templates/${editingId}`, { method: "PUT", body: JSON.stringify(form) });
        setInfo("Template aggiornato.");
      } else {
        await apiRequest("/document-templates", { method: "POST", body: JSON.stringify(form) });
        setInfo("Template creato.");
      }
      setForm(emptyForm); setEditingId(null);
      await load();
    } catch (err) { setError(err.message); }
  };

  const startEdit = (t) => {
    setForm({ name: t.name, template_type: t.template_type, description: t.description || "", body: t.body, is_active: t.is_active });
    setEditingId(t.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare il template?")) return;
    try { await apiRequest(`/document-templates/${id}`, { method: "DELETE" }); await load(); }
    catch (err) { setError(err.message); }
  };

  const selectedTemplate = templates.find((t) => t.id === renderState.templateId);

  const loadHistory = async (templateId) => {
    if (!templateId) { setHistory({ templateId: "", versions: [], renderLogs: [] }); return; }
    try {
      const [versions, renderLogs] = await Promise.all([
        apiRequest(`/document-templates/${templateId}/versions`).catch(() => []),
        canAudit ? apiRequest(`/document-templates/${templateId}/render-logs`).catch(() => []) : Promise.resolve([]),
      ]);
      setHistory({ templateId, versions, renderLogs });
    } catch (err) { /* ignore */ }
  };

  const renderTemplate = async () => {
    if (!renderState.templateId) { setError("Seleziona un template"); return; }
    setError(""); setInfo("");
    try {
      const result = await apiRequest(`/document-templates/${renderState.templateId}/render`, {
        method: "POST",
        body: JSON.stringify({
          company_id: renderState.companyId || null,
          variables: renderState.variables,
          save_as_document: renderState.save,
          document_name: renderState.document_name,
        }),
      });
      setRenderState({ ...renderState, result });
      if (result.saved_document_id) setInfo(`Documento salvato nel modulo Documenti (id ${result.saved_document_id.slice(0, 8)}...)`);
    } catch (err) { setError(err.message); }
  };

  return (
    <div className="space-y-6" data-testid="templates-page">
      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">Template documentali</p>
        <h2 className="mt-2 font-heading text-4xl font-bold text-slate-950">Modelli personalizzabili</h2>
        <p className="mt-2 text-slate-600">Crea modelli di nomine, verbali e comunicazioni con placeholder <code className="rounded bg-slate-100 px-1 text-xs">{`{{nome_variabile}}`}</code>. Variabili azienda risolte automaticamente: <code className="text-xs">company_name</code>, <code className="text-xs">company_vat</code>, <code className="text-xs">company_address</code>, <code className="text-xs">company_ateco</code>, <code className="text-xs">today</code>, <code className="text-xs">user_name</code>.</p>
      </section>

      {error && <Alert variant="destructive" data-testid="templates-error"><AlertTriangle className="h-4 w-4" /><AlertTitle>Errore</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>}
      {info && <Alert className="border-emerald-200 bg-emerald-50 text-emerald-900" data-testid="templates-info"><CheckCircle2 className="h-4 w-4" /><AlertTitle>OK</AlertTitle><AlertDescription>{info}</AlertDescription></Alert>}

      {canManage && (
        <Card className="shadow-none" data-testid="template-form-card">
          <CardHeader><CardTitle className="font-heading flex items-center gap-2"><Plus className="h-5 w-5 text-blue-900" /> {editingId ? "Modifica template" : "Nuovo template"}</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={submit} className="grid gap-3 md:grid-cols-2" data-testid="template-form">
              <div className="space-y-2"><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required minLength={2} placeholder="Es. Nomina RSPP standard" data-testid="template-name-input" /></div>
              <div className="space-y-2"><Label>Tipo</Label><select className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" value={form.template_type} onChange={(e) => setForm({ ...form, template_type: e.target.value })} data-testid="template-type-select">{TEMPLATE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}</select></div>
              <div className="space-y-2 md:col-span-2"><Label>Descrizione</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Quando si usa, riferimenti normativi, ecc." data-testid="template-description-input" /></div>
              <div className="space-y-2 md:col-span-2"><Label>Corpo del template</Label><Textarea rows={10} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required minLength={10} placeholder={"Esempio:\nIl sottoscritto {{datore_lavoro}}, in qualità di datore di lavoro della società {{company_name}} (P.IVA {{company_vat}}), in data {{today}} nomina il sig. {{rspp_name}} quale RSPP."} className="font-mono text-sm" data-testid="template-body-input" /></div>
              <label className="flex items-center gap-2 text-sm md:col-span-2"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="template-active-checkbox" /> Template attivo (utilizzabile per render)</label>
              <div className="flex gap-2 md:col-span-2">
                <Button type="submit" className="bg-blue-900 hover:bg-blue-800" data-testid="template-submit-button"><Save className="h-4 w-4" /> {editingId ? "Aggiorna" : "Crea"}</Button>
                {editingId && <Button type="button" variant="outline" onClick={() => { setForm(emptyForm); setEditingId(null); }} data-testid="template-cancel-button">Annulla</Button>}
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card className="shadow-none" data-testid="templates-list-card">
        <CardHeader><CardTitle className="font-heading flex items-center gap-2"><FileText className="h-5 w-5 text-blue-900" /> Template disponibili · {templates.length}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {templates.length === 0 && <p className="rounded-md border border-dashed border-slate-300 p-6 text-sm text-slate-500" data-testid="templates-empty">Nessun template ancora creato.</p>}
          {templates.map((t) => (
            <div key={t.id} className="flex flex-col gap-3 rounded-md border border-slate-200 p-4 md:flex-row md:items-center md:justify-between" data-testid={`template-row-${t.id}`}>
              <div>
                <p className="font-semibold text-slate-950" data-testid={`template-name-${t.id}`}>{t.name}</p>
                <p className="text-sm text-slate-500" data-testid={`template-meta-${t.id}`}>{TEMPLATE_TYPES.find((tt) => tt.value === t.template_type)?.label || t.template_type} · {t.placeholders.length} placeholder · {t.is_active ? "attivo" : "disattivato"}</p>
                {t.description && <p className="mt-1 text-sm text-slate-600">{t.description}</p>}
                <div className="mt-2 flex flex-wrap gap-1">
                  {t.placeholders.map((p) => <Badge key={p} className="bg-blue-50 text-blue-900 font-mono text-xs">{`{{${p}}}`}</Badge>)}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setRenderState({ ...renderState, templateId: t.id, variables: {}, result: null })} data-testid={`template-use-${t.id}`}><Wand2 className="h-4 w-4" /> Usa</Button>
                <Button variant="outline" size="sm" onClick={() => loadHistory(t.id)} aria-label="Cronologia versioni e rendering" data-testid={`template-history-${t.id}`}><History className="h-4 w-4" /> Cronologia v{t.version_number || 1}</Button>
                {canManage && <Button variant="outline" size="sm" onClick={() => startEdit(t)} data-testid={`template-edit-${t.id}`}>Modifica</Button>}
                {canManage && <Button variant="outline" size="sm" className="text-red-700" onClick={() => remove(t.id)} data-testid={`template-delete-${t.id}`}><Trash2 className="h-4 w-4" /></Button>}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {selectedTemplate && (
        <Card className="shadow-none" data-testid="template-render-card">
          <CardHeader><CardTitle className="font-heading flex items-center gap-2"><Sparkles className="h-5 w-5 text-blue-900" /> Genera documento da &quot;{selectedTemplate.name}&quot;</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-2"><Label>Azienda</Label><select className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" value={renderState.companyId} onChange={(e) => setRenderState({ ...renderState, companyId: e.target.value })} data-testid="render-company-select"><option value="">— Nessuna —</option>{companies.map((c) => <option key={c.id} value={c.id}>{c.business_name}</option>)}</select></div>
              <div className="space-y-2"><Label>Nome documento (per salvataggio)</Label><Input value={renderState.document_name} onChange={(e) => setRenderState({ ...renderState, document_name: e.target.value })} placeholder={`${selectedTemplate.name} ${new Date().getFullYear()}`} data-testid="render-document-name" /></div>
            </div>
            {selectedTemplate.placeholders.length > 0 && (
              <div className="space-y-2" data-testid="render-variables">
                <Label>Variabili manuali</Label>
                <div className="grid gap-2 md:grid-cols-2">
                  {selectedTemplate.placeholders.map((p) => (
                    <Input key={p} placeholder={`{{${p}}}`} value={renderState.variables[p] || ""} onChange={(e) => setRenderState({ ...renderState, variables: { ...renderState.variables, [p]: e.target.value } })} data-testid={`render-var-${p}`} />
                  ))}
                </div>
                <p className="text-xs text-slate-500">Le variabili azienda (company_name, company_vat, company_address, company_ateco), today e user_name sono risolte automaticamente quando selezioni un&apos;azienda.</p>
              </div>
            )}
            {renderState.companyId && (
              <label className="flex items-center gap-2 text-sm" data-testid="render-save-label">
                <input type="checkbox" checked={renderState.save} onChange={(e) => setRenderState({ ...renderState, save: e.target.checked })} data-testid="render-save-checkbox" />
                Salva il documento generato nel modulo Documenti (categoria automatica)
              </label>
            )}
            <Button className="bg-blue-900 hover:bg-blue-800" onClick={renderTemplate} data-testid="render-button"><Sparkles className="h-4 w-4" /> Genera</Button>
            {renderState.result && (
              <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-4" data-testid="render-result">
                <div className="flex flex-wrap gap-2">
                  <Badge className="bg-emerald-50 text-emerald-700" data-testid="render-resolved-count">{renderState.result.placeholders_resolved.length} placeholder risolti</Badge>
                  {renderState.result.placeholders_missing.length > 0 && <Badge className="bg-amber-50 text-amber-800" data-testid="render-missing-count">{renderState.result.placeholders_missing.length} non risolti: {renderState.result.placeholders_missing.join(", ")}</Badge>}
                </div>
                <pre className="whitespace-pre-wrap rounded-md border border-slate-200 bg-white p-3 text-sm" data-testid="render-output">{renderState.result.rendered_text}</pre>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      {history.templateId && (
        <Card className="shadow-none border-blue-200" data-testid="template-history-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading flex items-center gap-2"><History className="h-5 w-5 text-blue-900" /> Cronologia template</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setHistory({ templateId: "", versions: [], renderLogs: [] })} data-testid="template-history-close">Chiudi</Button>
            </div>
          </CardHeader>
          <CardContent className="grid gap-6 lg:grid-cols-2">
            <div data-testid="template-versions-panel">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{tr("templates.versions_title")} · {history.versions.length}</p>
              {history.versions.length === 0 ? (
                <p className="mt-3 rounded-md bg-slate-50 p-4 text-sm text-slate-500">{tr("templates.versions_empty")}</p>
              ) : (
                <ul className="mt-3 space-y-2">
                  {history.versions.map((v) => (
                    <li key={v.id} className="rounded-md border border-slate-200 p-3" data-testid={`version-row-${v.version_number}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <Badge className="bg-blue-50 text-blue-900">v{v.version_number}</Badge>
                        <span className="font-mono text-xs text-slate-500">{(v.created_at || "").slice(0, 19).replace("T", " ")}</span>
                      </div>
                      <p className="mt-2 text-sm font-medium text-slate-800">{v.changed_by_name || v.changed_by}</p>
                      {v.change_note && <p className="mt-1 text-xs text-slate-600">{v.change_note}</p>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {canAudit && (
              <div data-testid="template-render-logs-panel">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{tr("templates.render_logs_title")} · {history.renderLogs.length}</p>
                {history.renderLogs.length === 0 ? (
                  <p className="mt-3 rounded-md bg-slate-50 p-4 text-sm text-slate-500">{tr("templates.render_logs_empty")}</p>
                ) : (
                  <ul className="mt-3 space-y-2 max-h-96 overflow-y-auto">
                    {history.renderLogs.map((log) => (
                      <li key={log.id} className="rounded-md border border-slate-200 p-3" data-testid={`render-log-row-${log.id}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <Badge className="bg-emerald-50 text-emerald-700">v{log.template_version}</Badge>
                          <span className="font-mono text-xs text-slate-500">{(log.rendered_at || "").slice(0, 19).replace("T", " ")}</span>
                        </div>
                        <p className="mt-2 text-sm font-medium text-slate-800">{log.rendered_by_name} <span className="text-xs text-slate-500">({log.rendered_by_role})</span></p>
                        <p className="mt-1 text-xs text-slate-600">{log.placeholders_resolved.length} risolti · {log.placeholders_missing.length} mancanti{log.saved_document_id ? ` · salvato (${log.saved_document_id.slice(0, 8)})` : ""}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
